import { AfterViewInit, Component, computed, OnDestroy, OnInit, Signal, signal, ViewChild, WritableSignal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { ProductList } from 'src/app/classes/Products.class';
import { ONLY_IN_STOCK_FIELD, ONLY_IN_STOCK_OPTIONS, ONLY_IN_STORE_FIELD } from 'src/app/models/constants/only-in-stock.constant';
import { ItemSizeSelector } from 'src/app/models/enums/ItemSizeSelector.enum';
import { OnlyInStock } from 'src/app/models/enums/OnlyInStock.enum';
import { Route, UrlParams } from 'src/app/models/enums/Route.enum';
import { StoreConfiguration } from 'src/app/models/types/Configuration/StoreConfiguration.type';
import { Filter, Filters } from 'src/app/models/types/Filters.type';
import { AddOn, Categories, Category } from 'src/app/models/types/Rule/Rule.type';
import { Tab, Tabs } from 'src/app/models/types/Tabs.type';
import { SharedModule } from 'src/app/modules/shared.module';
import { ProductsService } from 'src/app/services/api/products.service';
import { RulesService } from 'src/app/services/api/rules.service';
import { AuthService } from 'src/app/services/auth.service';
import { DrawerService } from 'src/app/services/drawer.service';
import { FiltersService } from 'src/app/services/filters.service';
import { LoaderService } from 'src/app/services/loader.service';
import { ScrollService } from 'src/app/services/scroll.service';
import { ToastService } from 'src/app/services/toast.service';
import { DrawerComponent } from '../drawer/drawer.component';
import { FiltersComponent } from '../filters/filters.component';
import { OnlyInStockComponent } from '../filters/only-in-stock/only-in-stock.component';
import { ItemSizeSelectorComponent } from '../item-size-selector/item-size-selector.component';
import { TabHeaderComponent } from '../tab-header/tab-header.component';
import { BreadcrumbComponent } from '../breadcrumb/breadcrumb.component';
import { DynatraceService } from 'src/app/services/dynatrace.service';
import { AttachmentsModalComponent } from '../modals/attachments-modal/attachments-modal.component';
import { SharedService } from 'src/app/services/shared.service';

@Component({
  selector: 'app-products-page',
  imports: [
    SharedModule,
    TabHeaderComponent,
    ItemSizeSelectorComponent,
    AttachmentsModalComponent,
    DrawerComponent,
    FiltersComponent,
    OnlyInStockComponent
  ],
  // providers: [FiltersService],
  templateUrl: './products-page.component.html',
  styleUrl: './products-page.component.scss'
})
export class ProductsPageComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('attachmentModal', { static: false }) attachmentModal!: AttachmentsModalComponent;

  private readonly _destroying$ = new Subject<void>();

  public get Route(): typeof Route {
    return Route;
  }

  public applyPortoRicoPezzotto: boolean = false;

  private _selectedCategory: Category | undefined;
  public get selectedCategory(): Category | undefined {
    return this._selectedCategory;
  }

  public get addOn(): AddOn | null {
    return this.rulesService.getAddOn(this.selectedCategory?.id)
  }

  public get shouldAddOnOpenPdfModal(): boolean {
    return this.rulesService.shouldAddOnOpenPdfModal(this.addOn);
  }

  get selectedRuleDescription(): string {
    let selectedRule = this.rulesService.selectedRule();
    return selectedRule?.name || '';
  }

  private _discoverCategory: Categories | undefined;
  public get discoverCategory(): Categories | undefined { return this._discoverCategory }

  private _tabs: Tabs = [];
  public get tabs(): Tabs { return this._tabs; }

  public productList: ProductList = new ProductList();

  private _productSize: WritableSignal<ItemSizeSelector> = signal(ItemSizeSelectorComponent.getSizeFromLocalStorage());
  public productSizeClass: Signal<string> = computed(() => this.getProductSizeClass());

  constructor(
    public readonly authService: AuthService,
    public readonly loaderService: LoaderService,
    public readonly filtersService: FiltersService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly rulesService: RulesService,
    private readonly toastService: ToastService,
    private readonly sharedService: SharedService,
    private readonly drawerService: DrawerService,
    private readonly scrollService: ScrollService,
    private readonly productsService: ProductsService,
    private readonly dynatraceService: DynatraceService,
  ) { }

  ngOnInit(): void {

    this.filtersService.submit$
      .pipe(takeUntil(this._destroying$))
      .subscribe(value => {
        this.handleSearch(value);
        this.drawerService.close();
      });

    combineLatest([
      this.route.paramMap,
      this.authService.storeSession$,
      this.authService.userSession$
    ])
      .pipe(takeUntil(this._destroying$))
      .subscribe(([params, store, user]) => {
        let ruleId = params.get(UrlParams.Rule);
        this.rulesService.loadSelectedRule(ruleId);
        let categoryId = params.get(UrlParams.Category);
        let isChangedCategory = false;
        if (categoryId) {
          if (this._selectedCategory) {
            isChangedCategory = +categoryId !== this._selectedCategory.id;
          }
          let category = this.rulesService.rulesMap.get(parseInt(categoryId));
          let discoverCategory;
          let parentId = category!.parentId;
          if (parentId) {
            discoverCategory = this.rulesService.rulesMap.get(parentId);
          }
          this._selectedCategory = category;
          if (category) {
            this.dynatraceService.logCategoryName(category);
          }
          this._discoverCategory = discoverCategory;
          this.loadTabs();
          this.productList.reset();

          // if (this.authService.user?.isNotUserStore) {
          //   store = this.authService.getDefaultStore();
          // }

          if (user && store) {
            this.loadOnlyInStockOptions(store);

            const viewAll: Filter = {
              field: ONLY_IN_STOCK_FIELD,
              values: ["false", store.codeCompany]
            }

            const myStoreStock: Filter = {
              field: ONLY_IN_STORE_FIELD,
              values: ["true", `${store.idStore}`]
            }

            let opt = this.filtersService.getOnlyInStockOptionFromLocalStorage();
            this.filtersService.setOnlyInStockFilter(opt);

            //Se NON sono un utente STORE allora carico lo stock del mio store
            if (!(this.authService.user?.isUserStore)) {
              this.filtersService.setOnlyInStockFilter(viewAll, true);
            } else {
              //Se SONO utente STORE allore carico l'opzione selezionata in precedenza, se esiste, altrimenti imposto MyStoreStock
              if (opt === null) {
                this.filtersService.setOnlyInStockFilter(myStoreStock);
              } else {
                this.filtersService.setOnlyInStockFilter(opt);
              }

              //Se sono nello store di PORTO RICO, devo fare il pezzotto
              this.applyPortoRicoPezzotto = FiltersService.STORE_PEZZOTTI_COME_PORTO_RICO.includes(this.authService.store?.codeStore || '');
              if (this.applyPortoRicoPezzotto) {
                this.filtersService.setOnlyInStockFilter(myStoreStock);
              }
            }
          }
          // else if(this.authService.user?.isUserGenerallyHQ){

          // }

          //Se ho cambiato catgegory resetto i filtri e faccio la submit
          if (isChangedCategory) {
            this.productList.reset();
            this.filtersService.reset();
          } else {
            //faccio la submit e resetto i filtri nel caso non sto già effettuando una ricerca
            if (!this.productList.searching) {
              this.filtersService.reset(true);
            } else {
              this.filtersService.submit();
            }
          }
        }
      });

    this.filtersService.submit();
  }

  ngAfterViewInit(): void {
    this.scrollService.mountScrollService();
    if (this.scrollService.hasScrollReachedEnd$) {
      this.scrollService.hasScrollReachedEnd$.pipe(
        takeUntil(this._destroying$)
      ).subscribe((hasScrollReachedEnd: boolean) => {
        if (hasScrollReachedEnd) {
          this.loadNextPage();
        }
      });
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  loadOnlyInStockOptions(store: StoreConfiguration) {
    //Di default setto solo l'option ViewAll
    let options = [OnlyInStock.ViewAll];
    //Se NON SONO un utente store allora setto le options con tutte le opzioni
    if (this.authService.user?.isUserStore) {
      options = [...ONLY_IN_STOCK_OPTIONS];
      //Imposto MyStoreStock come default
    }
    let { codeCompany, idStore } = store;
    this.filtersService.setOnlyInStockOptions(
      options.map(opt => {
        let field = "OnlyInStock";
        let values: string[] = [];
        switch (opt) {
          // {
          //    "field" : "OnlyInStock",
          //    "values" : ["false",<codice company>]
          // }
          case OnlyInStock.ViewAll: {
            values = ["false", codeCompany];
            break;
          }
          // {
          //   "field" : "OnlyInStock",
          //   "values" : ["true",<codice company>]
          // }
          case OnlyInStock.CountryRegionStock: {
            values = ["true", codeCompany];
            break;
          }
          // {
          //   "field" : "OnlyInStore",
          //   "values" : ["true",<id store>]
          // }
          case OnlyInStock.MyStoreStock: {
            field = "OnlyInStore";
            values = ["true", idStore.toString()]
            break;
          }
        }
        return { field, values };
      })
    );
  }

  loadData() {
    this.loadProducts();
    this.loadFilters();
  }

  loadProducts() {
    if (this._selectedCategory) {
      let ruleId = this._selectedCategory.id;
      this.loaderService.show();
      this.productsService.getProducts(ruleId, this.productList.bodyPayload).subscribe({
        next: (res) => {
          this.productList.update(res);
          this.loaderService.hide();
        },
        error: (err) => {
          this.productList.reset();
          this.toastService.showError("Unable to load products");
          this.loaderService.hide();
        }
      })
    }
  }

  loadFilters() {
    if (this._selectedCategory) {
      let ruleId = this._selectedCategory.id;
      this.loaderService.show();
      this.productsService.getProductsFilters(ruleId, this.productList.filters).subscribe({
        next: (res) => {
          this.filtersService.setFilters(res);
          this.loaderService.hide();
        },
        error: (err) => {
          this.filtersService.setFilters([]);
          this.toastService.showError("Unable to load filters");
          this.loaderService.hide();
        }
      })
    }
  }

  loadNextPage() {
    if (this.productList.canLoadAnotherPage) {
      this.productList.skipToNextPage();
      this.loadProducts();
    }
  }

  loadTabs() {
    if (this.discoverCategory) {
      this._tabs = this.discoverCategory.childs.map(child => {
        return {
          id: child.id,
          name: child.name,
          routerLink: this.rulesService.getCategoryPageUrl(child),
        } as Tab;
      });
    }
  }

  handleChangeSize(size: ItemSizeSelector) {
    this._productSize.set(size);
  }

  handleSearchClick() {
    this.drawerService.open();
  }

  handleSearch(filters: Filters) {
    this.productList.setFilters(filters);
    this.loadData();
  }

  handleReset() {
    console.log('resetting');
    this.filtersService.reset();
  }

  getProductSizeClass(): string {
    switch (this._productSize()) {
      case ItemSizeSelector.Small: return 'sm';
      case ItemSizeSelector.Medium: return 'md';
      case ItemSizeSelector.Large: return 'lg';
      default: return '';
    }
  }

  showTitle(i: number) {
    return i !== 0 && this.productList.groupIndexes.includes(i);
  }

  handleBannerClick() {
    if (this.shouldAddOnOpenPdfModal) {
      // this.openPdfModal();
      let pdfs = this.addOn?.mediaPdf;
      if (pdfs && pdfs[0]) {
        this.sharedService.navigateTo_PdfPage(pdfs[0].url);
      }

    } else {
      this.router.navigate([Route.AddOn], { relativeTo: this.route });
    }
  }

  openPdfModal() {
    let urls = this.addOn?.mediaPdf?.map(pdf => pdf.url);
    this.attachmentModal.openModal(urls || []);
  }

}
